import { useSelector, useDispatch } from "react-redux";
import { getUserHashId, getUserName } from "store/userSlice";
import { Fragment, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { errorMessage, formatDateTime, formatMoney, formatNumberTo, scrollInto, scrollToTop } from "helpers/common";
import { api } from "helpers/axios";
import { toast } from "helpers/toast";
import sanitize from "helpers/sanitize";
import merge from "lodash.merge";
import { useSearchParams } from "react-router-dom";
import CalculationResultTreeView from "./CalculationResultTreeView";
import CalculationErrorTreeView from "./CalculationErrorTreeView";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Field, Input, InputAffix, Modal, NativeSelect, Select } from "components/ui";
import { useTranslation } from "translation/useTranslation";

const payout_conditions_distribution = {
  PERFORMANCE_RELATED: 'calculation_settings.benefit_distribution_type.performance_related_only',
  FLOOR_PERCENT_OR_AMOUNT: 'calculation_test.performance_related_plus_floor',
  FLOOR_ONLY: 'calculation_settings.benefit_distribution_type.floor_only'
};

const calculation_model = {
  '': '',
  POINT_VALUE_MODEL_VARIABLE: 'calculation_settings.calculation_method.point_value_model',
  POINT_VALUE_MODEL_FIXED_WITH_BUDGET: 'calculation_settings.calculation_method.point_value_model_fixed_point_with_budget',
  POINT_VALUE_MODEL_FIXED_WITHOUT_BUDGET: 'calculation_settings.calculation_method.point_value_model_fixed_point_without_budget',
  GUARANTEE_MODEL_WITH_BUDGET: 'calculation_settings.calculation_method.guarantee_model_with_budget_cap',
  GUARANTEE_MODEL_WITHOUT_BUDGET: 'calculation_settings.calculation_method.guarantee_model_with_no_budget_cap'
};

const distribution_method = {
  '': '',
  PERFORMANCE_OVER_HUNDRED: 'calculation_settings.guarantee_model_with_budget_cap_type.variable_distribution_performance_over_hundred',
  PERFORMANCE_RESULT: 'calculation_settings.guarantee_model_with_budget_cap_type.variable_distribution_performance_result',
  PER_CAPITA: 'calculation_settings.guarantee_model_with_budget_cap_type.variable_distribution_according_to_capita',
  PERFORMANCE_AND_SALARY: 'calculation_settings.guarantee_model_with_budget_cap_type.variable_distribution_performance_and_salary'
};

const remaining_distribution = {
  '': '',
  PERFORMANCE_RESULT: 'calculation_settings.how_to_distribute_remaining_budget_select.performance_result',
  HEADS: 'calculation_settings.payment_method.by_heads',
  PERFORMANCE_RESULT_PAY_FACTOR_REDUCTION_FACTOR: 'calculation_settings.how_to_distribute_remaining_budget_select.performance_result_pay_factor_reduction_factor'
};

const remaining_considered_employees = {
  ALL: 'calculation_settings.remaining_budget_applied_to.all_employees',
  NOT_CAPPED: 'calculation_settings.remaining_budget_applied_to.employee_with_no_cap_remunation'
};

const floor_payout_method = {
  '': '',
  BY_HEAD: 'calculation_settings.payment_method.by_heads',
  BY_REDUCTION_FACTORS: 'calculation_settings.payment_method.by_reduction_factors'
};

const floor_considered_employees = {
  ALL: 'calculation_settings.employee_selection_criteria.all_employees',
  NOT_EXCLUDED_WITHOUT_MINIMUM_PERFORMANCE: 'calculation_settings.employee_selection_criteria.eligible_employees.eligible_employees_without_min_benefit',
  NOT_EXCLUDED: 'calculation_settings.employee_selection_criteria.eligible_employees.eligible_employees'
};

const defaultEmployees = [
  { name: 'Kai', uuid: '3e424b82-c1d1-4ca8-bd3e-e27eac64fbfc' },
  { name: 'Daniel', uuid: '72b04a0a-825d-4270-8529-b73aa4492d79' },
  { name: 'Marcel', uuid: 'bfd206a7-c66d-4a94-b237-d62fba684450' },
  { name: 'Tim', uuid: 'dd825172-02d5-42e1-81a7-0f4e1adbabf7' },
  { name: 'Mike', uuid: '8069815a-8718-4a21-bb17-d6ca6059e8cd' }
];

export default function CalculationTest() {
  // const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const { translate, editableTranslate } = useTranslation();

  const userHashId = useSelector(getUserHashId);
  const userName = useSelector(getUserName);

  const [error, setError] = useState(null);

  const [calculationSettingsOptions, setCalculationSettingsOptions] = useState([]);
  const [calculationSettingsValue, setCalculationSettingsValue] = useState(null);

  const [user, setUser] = useState(null);
  const [name, setName] = useState(null);
  const [description, setDescription] = useState(null);
  const [resultVerifiedAt, setResultVerifiedAt] = useState(null);
  const [resultVerifiedChecked, setResultVerifiedChecked] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [calculationSettings, setCalculationSettings] = useState(null);
  const [floorFactorsArr, setFloorFactorsArr] = useState([]);

  const [employees, setEmployees] = useState(null);

  const [calculationSaveLoading, setCalculationSaveLoading] = useState(false);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);

  const [calculationResultData, setCalculationResultData] = useState(null);
  const [calculationResultSummary, setCalculationResultSummary] = useState(null);
  const [calculationResultTreeData, setCalculationResultTreeData] = useState(null);
  const [calculationErrorTreeData, setCalculationErrorTreeData] = useState(null);

  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [loadingSaveAs, setLoadingSaveAs] = useState(false);

  const defaultSetting = {
    budget_amount: '',
    calculation_model: Object.keys(calculation_model)[0],
    fixed_point_value: '',
    distribution_method: Object.keys(distribution_method)[0],
    ceiling_checked: false,
    remaining_distribution: Object.keys(remaining_distribution)[0],
    // remaining_considered_employees: Object.keys(remaining_considered_employees)[0],
    payout_conditions_distribution: Object.keys(payout_conditions_distribution)[0],
    floor: '',
    floor_type: 'FLOOR_AMOUNT',
    floor_payout_method: Object.keys(floor_payout_method)[0],
    floor_considered_employees: Object.keys(floor_considered_employees)[0],
    floor_factors: { performance_result: false, pay: false, work_time: false, missing_days: false, missing_months: false, weighting: false }
  };

  useEffect(() => {
    getTemplates(true);
  }, []);

  useEffect(() => {
    document.body.style.overflowY = (showSaveAsModal ? 'hidden' : 'auto');
  }, [showSaveAsModal]);

  useEffect(() => {
    if (calculationResult) {
      scrollInto('#result-div');
    }
  }, [calculationResult]);

  const handleCalculationSettingsChange = (e) => {
    setCalculationSettingsValue(e);
    setError(null);
    getTest(e.value);

    if (e?.value !== 'new') {
      searchParams.set('template', e.value);
      setSearchParams(searchParams);
    } else {
      setSearchParams({});
    }
  };

  const getTemplates = async (checkQuery = false) => {
    try {
      const { data, status } = await api.get(`/calculation-engine/tests`);
      if (status === 200) {
        const options = data.data.map(d => ({ value: d.uuid, label: d.name, hash_id: d.hash_id, result_verified_at: d.result_verified_at }));
        options.push({ value: 'new', label: translate('form.add_new', 'Neu hinzufügen') });

        setCalculationSettingsOptions(options);

        if (checkQuery && searchParams.get('template')) {
          const filtered = options.filter(o => o.value === searchParams.get('template'));
          const opt = (filtered.length > 0 ? filtered[0] : null);
          if (opt) {
            setCalculationSettingsValue(opt);
            getTest(opt.value);
          } else {
            setSearchParams({});
          }
        }
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleParameterChange = (e, key) => {
    let val = e.target.value;

    const newCalculationSettings = { ...calculationSettings };
    newCalculationSettings[key] = val;

    setCalculationSettings(newCalculationSettings);
  };

  const handleCeilingCheckedChange = (e) => {
    const checked = e.target.checked;

    const newCalculationSettings = { ...calculationSettings };
    newCalculationSettings.ceiling_checked = checked;

    setCalculationSettings(newCalculationSettings);
  };

  const handleFloorFactorsChange = (e, key) => {
    const checked = e.target.checked;

    const newCalculationSettings = { ...calculationSettings };
    newCalculationSettings.floor_factors[key] = checked;

    setCalculationSettings(newCalculationSettings);
  };

  const handleTableInputChange = (e, i, key, lkIndex = null) => {
    let val = e.target.value;
    if (['considered_for_calculation', 'excluded'].includes(key)) {
      val = e.target.checked;
    }

    const newEmployees = [...employees];
    if (lkIndex === null) {
      newEmployees[i][key] = val;

      if (key === 'expected_outcome' && calculationResultData) { // reset matched when change detected
        const newCalculationResultData = [...calculationResultData];
        if (newCalculationResultData?.[i]?.hasOwnProperty('matched')) {
          delete newCalculationResultData[i].matched;
          setCalculationResultData(newCalculationResultData);
        }
      } else if (key === 'ceiling_amount') {
        // calculate the bsc_ceiling_amount for each balanced scorecard based on weighting
        newEmployees[i].balanced_scorecards.forEach((bsc, index) => {
          if (bsc.weighting > 0) {
            bsc.bsc_ceiling_amount = newEmployees[i].ceiling_amount * (bsc.weighting / 100);
            newEmployees[i].balanced_scorecards[index] = bsc;
          }
        });
      }
    } else {
      if (['weighting', 'performance_result', 'fixed_amount'].includes(key)) {
        newEmployees[i].balanced_scorecards[lkIndex][key] = val;

        if (key === 'weighting') {
          // recalculate the bsc_ceiling_amount based on weighting
          if (newEmployees[i].ceiling_amount && val > 0) {
            newEmployees[i].balanced_scorecards[lkIndex].bsc_ceiling_amount = newEmployees[i].ceiling_amount * (val / 100);
          } else {
            newEmployees[i].balanced_scorecards[lkIndex].bsc_ceiling_amount = 0;
          }
        }
      } else {
        newEmployees[i].balanced_scorecards[lkIndex].factors[key] = val;
      }
    }

    setEmployees(newEmployees);
  };

  const setDefault = () => {
    setName(null);
    setDescription(null);
    setUser({ hash_id: userHashId, name: userName });
    setResultVerifiedAt(null);
    setResultVerifiedChecked(false);

    setCalculationSettings(defaultSetting);

    const sampleEmployees = [];

    defaultEmployees.forEach((obj, i) => {
      sampleEmployees.push({
        name: obj.name,
        uuid: obj.uuid,
        considered_for_calculation: (i > 5 ? false : true),
        ceiling_amount: 0,
        excluded: false,
        expected_outcome: null,
        balanced_scorecards: [
          { fixed_amount: 0, factors: { pay: 1, work_time: '0,3', missing_days: 1, missing_months: 1 }, weighting: 100, performance_result: 1, bsc_ceiling_amount: 0 },
          { fixed_amount: 0, factors: { pay: null, work_time: null, missing_days: null, missing_months: null }, weighting: null, performance_result: null, bsc_ceiling_amount: 0 },
          { fixed_amount: 0, factors: { pay: null, work_time: null, missing_days: null, missing_months: null }, weighting: null, performance_result: null, bsc_ceiling_amount: 0 }
        ]
      });
    });

    setEmployees(sampleEmployees);

    setCalculationResult(null);
    setCalculationResultData(null);
    setCalculationResultSummary(null);
  };

  const balancedScorecardClass = (i, index) => employees[i].balanced_scorecards[index]?.weighting ? '' : 'invisible';

  const balancedScorecardTrClass = (index) => {
    let foundActive = false;
    if (employees) {
      for (const employee of employees) {
        if (employee?.balanced_scorecards?.[index]?.weighting && employee?.considered_for_calculation) {
          foundActive = true;
          break;
        }
      }
    }
    if (foundActive) return '';
    else return 'd-none';
  };

  const expectedPayClass = (i) => {
    if (calculationResultData) {
      if (calculationResultData?.[i]?.matched === true) return 'matched';
      else if (calculationResultData?.[i]?.matched === false) return 'not-matched';
    }
    return '';
  }

  const getTest = async (uuid, loading = true) => {
    if (uuid === 'new') {
      setDefault();
      return;
    }

    if (loading) setCalculationSettings(null);

    try {
      const { data, status } = await api.get(`/calculation-engine/tests/${uuid}`);
      if (status === 200) {
        // merge default settings with existing settings to make sure all settings are available
        let calculationSettings = merge({}, defaultSetting, data.data.calculation_settings);

        setUser(data.user);
        setName(data.name);
        setDescription(data.description);
        setResultVerifiedAt(data.result_verified_at);
        setResultVerifiedChecked(data.result_verified_at ? true : false);
        setCalculationSettings(calculationSettings);
        setCalculationResult(null);
        setCalculationResultData(null);
        setCalculationResultSummary(null);

        // calculate bsc_ceiling_amount for each balanced scorecard based on weighting
        const newEmployees = [...data.data.employees];
        newEmployees.forEach((emp, i) => {
          newEmployees[i].balanced_scorecards.forEach((bsc, index) => {
            if (bsc.weighting > 0 && (newEmployees[i].ceiling_amount && newEmployees[i].ceiling_amount > 0)) {
              bsc.bsc_ceiling_amount = newEmployees[i].ceiling_amount * (bsc.weighting / 100);
              newEmployees[i].balanced_scorecards[index] = bsc;
            }
          });
        });
        setEmployees(newEmployees);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleResultVerifiedCheck = (e) => {
    const checked = e.target.checked;
    setResultVerifiedChecked(checked);
    if (!checked && resultVerifiedAt) {
      setResultVerifiedAt(null);
    }
  };

  const handleSave = async (asNew = false) => {
    setCalculationSaveLoading(true);
    setError(null);

    if (calculationSettings === null && employees === null) return;

    let saveName = name;

    if (asNew) {
      saveName = saveAsName;
    }

    try {
      const formData = {
        name: saveName,
        description,
        data: {
          calculation_settings: calculationSettings,
          employees: employees
        },
        result_verified: resultVerifiedChecked
      };

      if (!asNew && calculationSettingsValue && calculationSettingsValue.value !== 'new') {
        formData.hash_id = calculationSettingsValue.hash_id;
      }

      const { data, status } = await api.post(`/calculation-engine/tests`, formData);
      if (status === 200) {
        await getTemplates();

        searchParams.set('template', data.uuid);
        setSearchParams(searchParams);

        await getTest(data.uuid, false);

        setCalculationSettingsValue({
          value: data.uuid,
          hash_id: data.hash_id,
          label: data.name,
          result_verified_at: data.result_verified_at
        });

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      scrollToTop();
      setError(errorMessage(err, 'save calculation test'));
      Sentry.captureException(err);
    }
    setCalculationSaveLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const { status } = await api.delete(`/calculation-engine/tests/${calculationSettingsValue.hash_id}`);
      if (status === 200) {
        setCalculationSettingsValue(null);
        setName(null);
        setDescription(null);
        setUser(null);
        setResultVerifiedAt(null);
        setResultVerifiedChecked(false);
        setCalculationSettings(null);
        setEmployees(null);
        setCalculationResult(null);
        setCalculationResultData(null);
        setCalculationResultSummary(null);

        getTemplates();
        setSearchParams({});

        toast.success(translate('form.success.saved'));
      }

    } catch (err) {
      setError(errorMessage(err, 'delete calculation test'));
      Sentry.captureException(err);
    }
    setShowDeleteConfirm(false);
    setDeleteLoading(false);
  };

  const validate = () => {
    const errors = [];

    if (!calculationSettings.budget_amount) {
      errors.push(translate('calculation_test.budget_amount_required'));
    } else {
      const amount = parseFloat(formatNumberTo(calculationSettings.budget_amount || '', '.'));
      if (isNaN(amount) || amount < 0) {
        errors.push(translate('budget_setting.budget_amount') + ': ' + translate('user_import.min_0'));
      }
    }

    if (!calculationSettings.calculation_model) {
      errors.push(translate('calculation_test.calculation_model_required'));
    } else {
      if (['POINT_VALUE_MODEL_FIXED_WITH_BUDGET', 'POINT_VALUE_MODEL_FIXED_WITHOUT_BUDGET'].includes(calculationSettings.calculation_model) && calculationSettings.fixed_point_value) {
        const amount = parseFloat(formatNumberTo(calculationSettings.fixed_point_value || '', '.'));
        if (isNaN(amount) || amount < 0) {
          errors.push(translate('calculation_test.fixed_point_value') + ': ' + translate('user_import.min_0'));
        }
      }
    }

    if (calculationSettings.payout_conditions_distribution?.startsWith('FLOOR_') && calculationSettings.floor) {
      const amount = parseFloat(formatNumberTo(calculationSettings.floor || '', '.'));
      if (isNaN(amount) || amount < 0) {
        errors.push(translate('calculation_test.floor') + ': ' + translate('user_import.min_0'));
      } else if (calculationSettings?.floor_type === 'FLOOR_PERCENT' && amount > 100) {
        errors.push(translate('calculation_test.floor') + ': ' + translate('calculation_test.max_100'));
      }
    }

    const filteredEmployees = employees.filter(e => e.considered_for_calculation);

    if (filteredEmployees.length < 1) {
      errors.push(translate('calculation_test.at_least_1_employee'));
    }

    for (const employee of filteredEmployees) {
      const arr = [];

      let totalWeight = 0;

      employee.balanced_scorecards.forEach((bs, i) => {
        if (bs.weighting) {
          totalWeight += parseFloat(formatNumberTo(bs.weighting, '.'));

          for (const k in bs.factors) {
            const val = parseFloat(formatNumberTo(bs.factors[k] || '', '.'));
            if (k === 'pay') {
              if (!bs.factors[k] || isNaN(val) || val < 0) {
                arr.push('<li>' + translate('employee.balanced_scorecard') + ' ' + (i + 1) + ' - ' + translate('calculation_test.' + k) + ': ' + translate('user_import.min_0') + '</li>');
              }
            } else if (!bs.factors[k] || isNaN(val) || val < 0 || val > 1) {
              arr.push('<li>' + translate('employee.balanced_scorecard') + ' ' + (i + 1) + ' - ' + translate('calculation_test.' + k) + ': ' + translate('calculation_test.value_between_0_and_1') + '</li>');
            }
          }
        }
      });

      if (totalWeight !== 100) {
        arr.unshift('<li>' + translate('evaluation.validation.total_weight') + '</li>');
      }

      if (arr.length > 0) {
        errors.push(employee.name + ':<ul class="mb-0 pl-4">' + arr.join('') + '</ul>');
      }
    }

    if (errors.length > 0) {
      scrollToTop();
      setError(errors.join('<br />'));
      setCalculationLoading(false);
      return false;
    }

    return true;
  };

  const handleCalculate = async () => {
    setCalculationLoading(true);
    setError(null);
    setCalculationResult(null);
    setCalculationResultData(null);
    setCalculationResultSummary(null);
    setCalculationResultTreeData(null);
    setCalculationErrorTreeData(null);

    if (!validate()) return;

    try {
      const filteredEmployees = structuredClone(employees).filter(e => e.considered_for_calculation);
      for (const employee of filteredEmployees) {
        const lks = [];

        employee.balanced_scorecards.forEach((bs, i) => {
          if (bs.weighting) {
            if (!calculationSettings.ceiling_checked) {
              bs.bsc_ceiling_amount = 0;
            }

            lks.push(bs);
          }
        });

        if (!calculationSettings.ceiling_checked) {
          employee.ceiling_amount = 0;
        }

        employee.balanced_scorecards = lks;
      }

      let payout_distribution_type = calculationSettings.payout_conditions_distribution;
      if (payout_distribution_type === 'FLOOR_PERCENT_OR_AMOUNT') {
        payout_distribution_type = calculationSettings?.floor_type || 'FLOOR_AMOUNT';
      }

      const formData = {
        budget_amount: calculationSettings.budget_amount,
        calculation_setting: {
          mode: calculationSettings.calculation_model,
          fix_point_value: (['POINT_VALUE_MODEL_FIXED_WITH_BUDGET', 'POINT_VALUE_MODEL_FIXED_WITHOUT_BUDGET'].includes(calculationSettings.calculation_model) ? calculationSettings.fixed_point_value : null),
          distribution_method: calculationSettings.distribution_method,
          remaining_distribution: {
            type: calculationSettings.remaining_distribution,
            // considered_employee: (calculationSettings?.ceiling_checked ? calculationSettings.remaining_considered_employees : null)
          },
          payout_distribution_type,
          floor: {
            amount: (calculationSettings.payout_conditions_distribution?.startsWith('FLOOR_') ? calculationSettings.floor : null),
            payment_type: (calculationSettings.payout_conditions_distribution?.startsWith('FLOOR_') ? calculationSettings.floor_payout_method : null),
            considered_employee: (calculationSettings.payout_conditions_distribution?.startsWith('FLOOR_') ? calculationSettings.floor_considered_employees : null),
            considered_factors: (calculationSettings.payout_conditions_distribution?.startsWith('FLOOR_') ? calculationSettings.floor_factors : null)
          }
        },
        employees: filteredEmployees
      };

      const { data, status } = await api.post(`/calculation-engine/simulation`, formData);
      if (status === 200) {
        setCalculationResult(data);

        const resultData = [];
        if (data?.response?.employees) {
          setCalculationResultTreeData(data.response);
          for (const employee of employees) {
            if (!employee.considered_for_calculation) {
              resultData.push(null);
              continue;
            }

            data.response.employees.forEach(responseEmployee => {
              if (employee.uuid === responseEmployee.id) {
                const lks = [null, null, null];

                responseEmployee.scoreCards.forEach(responseLk => {
                  for (let i = 0; i < lks.length; i++) {
                    if (lks[i] === null && employee?.balanced_scorecards?.[i]?.weighting) {
                      const calculationResult = responseLk.calculationResult;
                      const performanceResult = (calculationResult.hasOwnProperty('performance') ? calculationResult.performance : null);
                      const resultPay = parseFloat(formatNumberTo((performanceResult && performanceResult.hasOwnProperty('pay') ? performanceResult.pay : ''), '.'));

                      lks[i] = {
                        amount: (performanceResult.hasOwnProperty('pay') ? formatMoney(performanceResult.pay) : null)
                      };

                      break;
                    }
                  }
                });

                const expectedPay = parseFloat(formatNumberTo(employee?.expected_outcome || '', '.'));
                const calculationResult = responseEmployee.calculationResult;
                const performanceResult = (calculationResult.hasOwnProperty('performance') ? calculationResult.performance : null);
                const floorResult = (calculationResult.hasOwnProperty('floor') ? calculationResult.floor : null);
                const cappingResult = (calculationResult.hasOwnProperty('capping') ? calculationResult.capping : null);
                const total = parseFloat(formatNumberTo((calculationResult.hasOwnProperty('total') ? calculationResult.total : ''), '.'));

                resultData.push({
                  total_amount: formatMoney(total),
                  balanced_scorecards: lks,
                  matched: (!isNaN(total) && !isNaN(expectedPay)) ? (
                    // set to two decimal places for comparison
                    formatMoney(total) === formatMoney(expectedPay)
                  ) : null,
                });
              }
            });
          }
          setCalculationResultData(resultData);
        } else if (data?.response?.hasOwnProperty('errors')) {
          setCalculationErrorTreeData(data.response);
        }

        if (data?.response?.logging) {
          for (const log of data.response.logging) {
            if (log.key === 'POINT_VALUE_VARIABLE' && log.details.length === 2) {
              const temp = log.details[1].split(' = ');

              setCalculationResultSummary({
                point_value: (temp[0] || '')
              });
              break;
            }
          }
        }

        const arr = [];
        for (const k in calculationSettings.floor_factors) {
          if (calculationSettings.floor_factors[k]) arr.push(k);
        }
        setFloorFactorsArr(arr);
      }
    } catch (err) {
      scrollToTop();
      setError(errorMessage(err, 'trigger calculation'));
      Sentry.captureException(err);
    }
    setCalculationLoading(false);
  };

  const incrementLastDigit = (str) => {
    const regex = /\d+$/;

    if (regex.test(str)) {
      const match = str.match(regex)[0];
      const incremented = parseInt(match) + 1;

      return str.replace(regex, incremented.toString());
    } else {
      return str + ' 1';
    }
  };

  const handleSaveAsModal = () => {
    setShowSaveAsModal(true);
    setSaveAsName(incrementLastDigit(name));
  };

  const handleHideSaveAsModal = () => {
    setShowSaveAsModal(false);
    setSaveAsName('');
  };

  const handleSubmitSaveAs = async (e) => {
    e.preventDefault();
    setLoadingSaveAs(true);

    await handleSave(true);

    setLoadingSaveAs(false);
    handleHideSaveAsModal();
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(translate('calculation_test.share_link_copied'));
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    if (typeof text !== 'string') {
      text = JSON.stringify(text, null, 2);
    }

    navigator.clipboard.writeText(text);
    toast.success(translate('calculation_test.copied_to_clipboard'));
  };

  return (
    <>
      <div className="col-lg col-md-12 d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h3>{editableTranslate('global.menu.calculation.calculation_test')}</h3>
          <div className="mt-4 calculation-test-div">
            {
              error &&
              <div className="mt-3">
                <ErrorAlert error={error} onClose={() => setError(null)} isHtml={true} />
              </div>
            }

            <div>
              <div className="form-row mb-3">
                <div className="form-group col-4">
                  <Field
                    label={editableTranslate('calculation_test.template', 'Vorlage')}
                    help={(calculationSettingsValue && !calculationSettings) && <div className="spinner-border spinner-border-sm ml-2" role="status">
                      <span className="sr-only">Wird geladen...</span>
                    </div>}
                  >
                    <div style={{ display: 'contents' }} data-testid="template-select">
                      <Select placeholder={translate('form.select_placeholder')} options={calculationSettingsOptions} value={calculationSettingsValue} onChange={handleCalculationSettingsChange} formatOptionLabel={data => <span>{data.label} {data.result_verified_at && <span className="ml-2"><i className="bi bi-check-circle-fill"></i> {formatDateTime(data.result_verified_at)}</span>}</span>} />
                    </div>
                  </Field>
                </div>
                {
                  (calculationSettingsValue && calculationSettings) && <>
                    <div className="form-group col-4">
                      <Field label={editableTranslate('calculation_test.created_by')}>
                        <Input data-testid="created-by-input" className="text-left" value={user?.name || ''} readOnly />
                      </Field>
                    </div>
                    <div className="form-group col-4">
                      <Field label={editableTranslate('calculation_test.name', 'Name')} htmlFor="calculation-test-name">
                        <InputAffix suffix={
                          (calculationSettingsValue.value !== 'new' && user?.hash_id === userHashId)
                            ? <button data-testid="delete-button" type="button" className="peb-input-affix__action" onClick={() => setShowDeleteConfirm(true)}><i className="bi bi-trash"></i></button>
                            : null
                        }>
                          <Input data-testid="name-input" id="calculation-test-name" type="text" className="text-left" value={name || ''} onChange={e => setName(e.target.value)} readOnly={user?.hash_id !== userHashId} />
                        </InputAffix>
                      </Field>
                    </div>
                    <div className="form-group col-4">
                      <Field label={editableTranslate('calculation_test.description', 'Beschreibung')} htmlFor="calculation-test-description">
                        <textarea data-testid="description-input" id="calculation-test-description" className="form-control" value={description || ''} onChange={e => setDescription(e.target.value)} readOnly={user?.hash_id !== userHashId}></textarea>
                      </Field>
                    </div>
                    <div className="form-group col-4">
                      <label className="peb-field__label">{editableTranslate('calculation_test.result_verified')}</label>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="result_verified" checked={resultVerifiedChecked} onChange={handleResultVerifiedCheck} disabled={user?.hash_id !== userHashId} data-testid="result-verified-checkbox" />
                        <label className="form-check-label ml-1" htmlFor="result_verified">{resultVerifiedAt ? formatDateTime(resultVerifiedAt) : ''}</label>
                      </div>
                    </div>
                    {
                      calculationSettingsValue.value !== 'new' && <div className="col">
                        <Button data-testid="share-button" variant="primary" onClick={handleShare}><i className="bi bi-link-45deg"></i> {editableTranslate('calculation_test.share')}</Button>
                      </div>
                    }
                  </>
                }
              </div>
            </div>

            {
              (calculationSettingsValue && calculationSettings) && <div className="gray-box filters-box">
                <h5>{editableTranslate('calculation_test.calculation_settings', 'Berechnungseinstellungen')}</h5>

                <div className="px-3 py-2 pb-3 calculation-settings-test-div">
                  <div className="calculation-settings-test-group">
                    <span className="group-title">{editableTranslate('calculation_test.calculation', 'Berechnung')}</span>

                    <hr />

                    <div className="form-row">
                      <div className="form-group col-4">
                        <Field label={editableTranslate('calculation_test.payout_conditions_distribution', 'Auszahlungsmethoden')}>
                          <NativeSelect data-testid="payout-conditions-distribution-select" value={calculationSettings?.payout_conditions_distribution || ''} onChange={e => handleParameterChange(e, 'payout_conditions_distribution')}>
                            {
                              Object.keys(payout_conditions_distribution).map((item, i) => <option key={i} value={item}>{translate(payout_conditions_distribution[item])}</option>)
                            }
                          </NativeSelect>
                        </Field>
                      </div>
                      <div className="form-group col-4">
                        <Field label={editableTranslate('budget_setting.budget_amount')}>
                          <Input data-testid="budget-amount-input" type="text" value={calculationSettings?.budget_amount || ''} onChange={e => handleParameterChange(e, 'budget_amount')} />
                        </Field>
                      </div>
                      <div className="form-group col-4">
                        <Field label={editableTranslate('calculation_test.calculation_model', 'Berechnungsmodell')}>
                          <NativeSelect data-testid="calculation-model-select" value={calculationSettings?.calculation_model || ''} onChange={e => handleParameterChange(e, 'calculation_model')}>
                            {
                              Object.keys(calculation_model).map((item, i) => <option key={i} value={item}>{translate(calculation_model[item])}</option>)
                            }
                          </NativeSelect>
                        </Field>
                      </div>
                      {
                        calculationSettings && ['POINT_VALUE_MODEL_FIXED_WITH_BUDGET', 'POINT_VALUE_MODEL_FIXED_WITHOUT_BUDGET'].includes(calculationSettings.calculation_model) &&
                        <div className="form-group col-4">
                          <Field label={editableTranslate('calculation_test.fixed_point_value', 'Fester Punktwert')}>
                            <Input data-testid="fixed-point-value-input" type="text" value={calculationSettings?.fixed_point_value || ''} onChange={e => handleParameterChange(e, 'fixed_point_value')} />
                          </Field>
                        </div>
                      }
                      <div className="form-group col-4">
                        <Field label={editableTranslate('calculation_test.distribution_method', 'Verteilungsmethode')}>
                          <NativeSelect data-testid="distribution-method-select" value={calculationSettings?.distribution_method || ''} onChange={e => handleParameterChange(e, 'distribution_method')}>
                            {
                              Object.keys(distribution_method).map((item, i) => <option key={i} value={item}>{translate(distribution_method[item])}</option>)
                            }
                          </NativeSelect>
                        </Field>
                      </div>
                      <div className="form-group col-4">
                        <Field label={editableTranslate('calculation_test.remaining_distribution', 'Restverteilung')}>
                          <NativeSelect data-testid="remaining-distribution-select" value={calculationSettings?.remaining_distribution || ''} onChange={e => handleParameterChange(e, 'remaining_distribution')}>
                            {
                              Object.keys(remaining_distribution).map((item, i) => <option key={i} value={item}>{translate(remaining_distribution[item])}</option>)
                            }
                          </NativeSelect>
                        </Field>
                      </div>
                    </div>
                  </div>

                  {
                    calculationSettings?.payout_conditions_distribution?.startsWith('FLOOR_') && <div className="calculation-settings-test-group">
                      <span className="group-title">{editableTranslate('calculation_test.floor', 'Untergrenze')}</span>

                      <hr />

                      <div className="form-row">
                        <div className="form-group col-4">
                          <label>
                            <NativeSelect data-testid="floor-type-select" size="sm" className="mb-1px" value={calculationSettings?.floor_type || ''} onChange={e => handleParameterChange(e, 'floor_type')}>
                              <option value="FLOOR_AMOUNT">{translate('calculation_test.floor_amount')}</option>
                              <option value="FLOOR_PERCENT">{translate('calculation_test.floor_percent')}</option>
                            </NativeSelect>
                          </label>
                          <InputAffix
                            prefix={(!calculationSettings?.floor_type || calculationSettings?.floor_type === 'FLOOR_AMOUNT') ? '€' : null}
                            suffix={calculationSettings?.floor_type === 'FLOOR_PERCENT' ? '%' : null}
                          >
                            <Input data-testid="floor-input" type="text" value={calculationSettings?.floor || ''} onChange={e => handleParameterChange(e, 'floor')} />
                          </InputAffix>
                        </div>
                        <div className="form-group col-4">
                          <Field label={editableTranslate('calculation_test.floor_payout_method', 'Verteilung')}>
                            <NativeSelect data-testid="floor-payout-method-select" value={calculationSettings?.floor_payout_method || ''} onChange={e => handleParameterChange(e, 'floor_payout_method')}>
                              {
                                Object.keys(floor_payout_method).map((item, i) => <option key={i} value={item}>{translate(floor_payout_method[item])}</option>)
                              }
                            </NativeSelect>
                          </Field>
                        </div>
                        <div className="form-group col-4">
                          <Field label={editableTranslate('calculation_test.floor_considered_employees', 'Zu berücksichtige Beschäftigte')}>
                            <NativeSelect data-testid="floor-considered-employees-select" value={calculationSettings?.floor_considered_employees || ''} onChange={e => handleParameterChange(e, 'floor_considered_employees')}>
                              {
                                Object.keys(floor_considered_employees).map((item, i) => <option key={i} value={item}>{translate(floor_considered_employees[item])}</option>)
                              }
                            </NativeSelect>
                          </Field>
                        </div>
                        <div className="form-group col">
                          <label className="peb-field__label">{editableTranslate('calculation_test.floor_factors', 'Faktoren')}</label>
                          <div>
                            <div className="form-check form-check-inline mr-4">
                              <input className="form-check-input" type="checkbox" id="factors_performance_result" value="performance_result" checked={calculationSettings?.floor_factors?.performance_result || false} onChange={e => handleFloorFactorsChange(e, 'performance_result')} data-testid="factors-performance-result-checkbox" />
                              <label className="form-check-label" htmlFor="factors_performance_result">{editableTranslate('calculation_test.performance_result', 'Leistungsergebnis')}</label>
                            </div>
                            <div className="form-check form-check-inline mr-4">
                              <input className="form-check-input" type="checkbox" id="factors_pay" value="pay" checked={calculationSettings?.floor_factors?.pay || false} onChange={e => handleFloorFactorsChange(e, 'pay')} data-testid="factors-pay-checkbox" />
                              <label className="form-check-label" htmlFor="factors_pay">{editableTranslate('calculation_test.pay')}</label>
                            </div>
                            <div className="form-check form-check-inline mr-4">
                              <input className="form-check-input" type="checkbox" id="factors_work_time" value="work_time" checked={calculationSettings?.floor_factors?.work_time || false} onChange={e => handleFloorFactorsChange(e, 'work_time')} data-testid="factors-work-time-checkbox" />
                              <label className="form-check-label" htmlFor="factors_work_time">{editableTranslate('calculation_test.work_time', 'Arbeitszeitfaktor (AZx)')}</label>
                            </div>
                            <div className="form-check form-check-inline mr-4">
                              <input className="form-check-input" type="checkbox" id="factors_missing_days" value="missing_days" checked={calculationSettings?.floor_factors?.missing_days || false} onChange={e => handleFloorFactorsChange(e, 'missing_days')} data-testid="factors-missing-days-checkbox" />
                              <label className="form-check-label" htmlFor="factors_missing_days">{editableTranslate('calculation_test.missing_days', 'Fehltagefaktor (FTx)')}</label>
                            </div>
                            <div className="form-check form-check-inline mr-4">
                              <input className="form-check-input" type="checkbox" id="factors_missing_months" value="missing_months" checked={calculationSettings?.floor_factors?.missing_months || false} onChange={e => handleFloorFactorsChange(e, 'missing_months')} data-testid="factors-missing-months-checkbox" />
                              <label className="form-check-label" htmlFor="factors_missing_months">{editableTranslate('calculation_test.missing_months', 'Fehlmonatefaktor (FMx)')}</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  <div className={`calculation-settings-test-group ${calculationSettings?.ceiling_checked ? '' : 'pb-3'}`}>
                    <span className="group-title">
                      <input className="d-inline-flex" type="checkbox" id="ceiling_checked" checked={calculationSettings?.ceiling_checked || false} onChange={e => handleCeilingCheckedChange(e)} data-testid="ceiling-checked-checkbox" />
                      <label className="mb-0 ml-1" htmlFor="ceiling_checked">{editableTranslate('calculation_test.ceiling', 'Deckelung')}</label>
                    </span>

                    {
                      calculationSettings?.ceiling_checked && <>
                        <hr />
                        <div className="form-row">
                          <div className="form-group col-4">
                            <label className="peb-field__label">{editableTranslate('calculation_test.capping_amount', 'Betrag')}</label>
                            <small><i>Ceiling amount is now in individual employee settings</i></small>
                          </div>
                          {/* <div className="form-group col-4">
                            <Field label={editableTranslate('calculation_test.remaining_considered_employees', 'Remaining considered employees')}>
                              <NativeSelect value={calculationSettings?.remaining_considered_employees || ''} onChange={e => handleParameterChange(e, 'remaining_considered_employees')}>
                                {
                                  Object.keys(remaining_considered_employees).map((item, i) => <option key={i} value={item}>{translate(remaining_considered_employees[item])}</option>)
                                }
                              </NativeSelect>
                            </Field>
                          </div> */}
                        </div>
                      </>
                    }
                  </div>
                </div>
              </div>
            }

            {
              (calculationSettingsValue && calculationSettings && employees) && <div className="mt-5">
                <h5>{editableTranslate('global.employees')}</h5>

                <div className="table-responsive">
                  <table className="table table-borderless" data-testid="employees-table">
                    <thead>
                      <tr>
                        <th></th>
                        {
                          employees && employees.map((employee, i) => {
                            return <th key={i} className={`text-center ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                              {employee.name} <br />
                              <small>
                                {/* only short uuid */}
                                <span title={employee.uuid} style={{ cursor: 'help' }}>{employee.uuid.split('-')[0]}</span>
                              </small>
                            </th>
                          })
                        }
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="font-weight-bold">{editableTranslate('calculation_test.consider_for_calculation', 'Bei der Berechnung berücksichtigen')}</td>
                        {
                          employees && employees.map((employee, i) => <td key={i} className={`${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                            <div className="d-flex justify-content-center">
                              <input type="checkbox" checked={employee.considered_for_calculation} onChange={e => handleTableInputChange(e, i, 'considered_for_calculation')} tabIndex={i} data-testid={`employee-${i}-considered-for-calculation`} />
                            </div>
                          </td>)
                        }
                      </tr>
                      {
                        calculationSettings?.ceiling_checked && <>
                          <tr>
                            <td className="font-weight-bold py-0">{editableTranslate('calculation_test.ceiling_amount', 'Deckelungsbetrag')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={`td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <InputAffix prefix="€">
                                  <Input data-testid={`employee-${employee.uuid}-ceiling-amount-input`} type="text" value={employee.ceiling_amount} onChange={e => handleTableInputChange(e, i, 'ceiling_amount')} tabIndex={i} />
                                </InputAffix>
                              </td>)
                            }
                          </tr>
                        </>
                      }
                      <tr>
                        <td className="font-weight-bold">{editableTranslate('calculation_test.excluded', 'Von Leistungsbewertung ausgeschlossen')}</td>
                        {
                          employees && employees.map((employee, i) => <td key={i} className={`${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                            <div className="d-flex justify-content-center">
                              <input type="checkbox" checked={employee.excluded} onChange={e => handleTableInputChange(e, i, 'excluded')} tabIndex={i} data-testid={`employee-${i}-excluded`} />
                            </div>
                          </td>)
                        }
                      </tr>
                      <tr>
                        <td colSpan={9} className="py-2">
                          <hr className="m-0" />
                        </td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold py-0">{editableTranslate('calculation_test.expected_outcome', 'Erwartetes Ergebnis')}</td>
                        {
                          employees && employees.map((employee, i) => <td key={i} className={`td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                            <InputAffix prefix="€">
                              <Input data-testid={`employee-${employee.uuid}-expected-outcome-input`} type="text" className={expectedPayClass(i)} value={employee.expected_outcome || ''} onChange={e => handleTableInputChange(e, i, 'expected_outcome')} tabIndex={i} />
                            </InputAffix>
                          </td>)
                        }
                      </tr>
                      <tr>
                        <td className="font-weight-bold py-0">{editableTranslate('calculation_test.total_performance_related_pay', 'Gesamtbetrag (aus der Berechnung)')}</td>
                        {
                          employees && employees.map((employee, i) => <td key={i} className={`td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                            <InputAffix prefix="€">
                              <Input data-testid={`employee-${employee.uuid}-total-amount-input`} type="text" value={calculationResultData?.[i]?.total_amount || ''} readOnly tabIndex={i} />
                            </InputAffix>
                          </td>)
                        }
                      </tr>
                      <tr>
                        <td colSpan={9} className="separator-td"></td>
                      </tr>
                      {
                        [0, 1, 2].map(index => <Fragment key={index}>
                          <tr>
                            <td className="font-weight-bold text-nowrap">{editableTranslate('employee.balanced_scorecard')} {index + 1}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={`td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <InputAffix suffix="%">
                                  <Input data-testid={`employee-${employee.uuid}-bsc-${index}-weighting-input`} type="text" value={employee.balanced_scorecards[index]?.weighting || ''} onChange={e => handleTableInputChange(e, i, 'weighting', index)} tabIndex={i} />
                                </InputAffix>
                              </td>)
                            }
                          </tr>
                          {
                            calculationSettings?.ceiling_checked && <tr className={balancedScorecardTrClass(index)}>
                              <td className="py-0">{editableTranslate('calculation_test.bsc_ceiling_amount', 'Deckelungsbetrag der Leistungskarte')}</td>
                              {
                                employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                  <InputAffix prefix="€">
                                    <Input data-testid={`employee-${employee.uuid}-bsc-${index}-ceiling-amount-input`} type="text" readOnly value={employee.balanced_scorecards[index]?.bsc_ceiling_amount || ''} tabIndex={i} />
                                  </InputAffix>
                                </td>)
                              }
                            </tr>
                          }

                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.fixed_amount', 'Festbetrag (FB)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <InputAffix prefix="€">
                                  <Input data-testid={`employee-${employee.uuid}-bsc-${index}-fixed-amount-input`} type="text" value={employee.balanced_scorecards[index]?.fixed_amount || ''} onChange={e => handleTableInputChange(e, i, 'fixed_amount', index)} tabIndex={i} />
                                </InputAffix>
                              </td>)
                            }
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.pay', 'Entgeltfaktor (EFx)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <Input data-testid={`employee-${employee.uuid}-bsc-${index}-pay-input`} type="text" size="sm" value={employee.balanced_scorecards[index]?.factors?.pay || ''} onChange={e => handleTableInputChange(e, i, 'pay', index)} tabIndex={i} />
                              </td>)
                            }
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.work_time', 'Arbeitszeitfaktor (AZx)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <Input data-testid={`employee-${employee.uuid}-bsc-${index}-work-time-input`} type="text" size="sm" value={employee.balanced_scorecards[index]?.factors?.work_time || ''} onChange={e => handleTableInputChange(e, i, 'work_time', index)} tabIndex={i} />
                              </td>)
                            }
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.missing_days', 'Fehltagefaktor (FTx)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <Input data-testid={`employee-${employee.uuid}-bsc-${index}-missing-days-input`} type="text" size="sm" value={employee.balanced_scorecards[index]?.factors?.missing_days || ''} onChange={e => handleTableInputChange(e, i, 'missing_days', index)} tabIndex={i} />
                              </td>)
                            }
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.missing_months', 'Fehlmonatefaktor (FMx)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <Input data-testid={`employee-${employee.uuid}-bsc-${index}-missing-months-input`} type="text" size="sm" value={employee.balanced_scorecards[index]?.factors?.missing_months || ''} onChange={e => handleTableInputChange(e, i, 'missing_months', index)} tabIndex={i} />
                              </td>)
                            }
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.result', 'Leistungsergebnis (LE)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <Input data-testid={`employee-${employee.uuid}-bsc-${index}-performance-result-input`} type="text" size="sm" value={employee.balanced_scorecards[index]?.performance_result || ''} onChange={e => handleTableInputChange(e, i, 'performance_result', index)} tabIndex={i} />
                              </td>)
                            }
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td colSpan={9} className="py-2">
                              <hr className="m-0" />
                            </td>
                          </tr>
                          <tr className={balancedScorecardTrClass(index)}>
                            <td className="py-0">{editableTranslate('calculation_test.performance_related_pay', 'Leistungsentgelt (LEB)')}</td>
                            {
                              employees && employees.map((employee, i) => <td key={i} className={balancedScorecardClass(i, index) + ` td-input ${!employee.considered_for_calculation ? 'td-not-considered' : ''}`}>
                                <InputAffix prefix="€">
                                  <Input data-testid={`employee-${employee.uuid}-bsc-${index}-amount-input`} type="text" value={calculationResultData?.[i]?.balanced_scorecards?.[index]?.amount || ''} readOnly tabIndex={i} />
                                </InputAffix>
                              </td>)
                            }
                          </tr>
                          <tr>
                            <td colSpan={9} className="separator-td"></td>
                          </tr>
                        </Fragment>)
                      }
                    </tbody>
                  </table>

                </div>
              </div>
            }

            {
              (calculationSettingsValue && calculationSettings) && <div className="settings-sticky-footer">
                <hr className="mt-0" />
                <div className="text-center">
                  {
                    calculationSettingsValue?.value !== 'new' && <Button data-testid="save-as-button" variant="secondary" onClick={handleSaveAsModal} className="mr-2">{editableTranslate('button.save_as')}</Button>
                  }

                  <Button variant="secondary" onClick={() => handleSave()} className={`mr-2 ${user?.hash_id !== userHashId ? 'cursor-not-allowed' : ''}`} loading={calculationSaveLoading} disabled={calculationLoading || user?.hash_id !== userHashId || (user?.hash_id === userHashId && resultVerifiedAt)} data-testid="save-button">
                    {editableTranslate('button.save')}
                  </Button>

                  <Button variant="primary" onClick={handleCalculate} loading={calculationLoading} disabled={calculationSaveLoading} data-testid="calculate-button">
                    {editableTranslate('calculation_test.calculate', 'Berechnen')}
                  </Button>
                </div>
              </div>
            }

            <div id="result-div"></div>

            {
              (calculationSettingsValue && calculationSettings && employees) && <hr className="my-4" />
            }

            {
              (calculationSettingsValue && calculationSettings && calculationResult) && <div className="mb-4">
                <div className="row">
                  <div className="col-6">
                    <div className="balancescorecard-setting-card">
                      <div className="card-setting-header font-weight-bold">{editableTranslate('calculation_test.calculation_parameters')}</div>
                      <div className="card-setting-body mt-3">
                        <div className="summary-section">
                          <span className="summary-title">{editableTranslate('calculation_test.calculation', 'Berechnung')}</span>

                          <div className="row">
                            <div className="col-6 card-body-key">{editableTranslate('calculation_test.payout_conditions_distribution', 'Auszahlungsmethoden')}</div>
                            <div className="col-6 card-body-value">{editableTranslate(payout_conditions_distribution[calculationSettings?.payout_conditions_distribution || ''])}</div>
                          </div>
                          <div className="row">
                            <div className="col-6 card-body-key">{editableTranslate('budget_setting.budget_amount')}</div>
                            <div className="col-6 card-body-value">{calculationSettings?.budget_amount || ''}</div>
                          </div>
                          <div className="row">
                            <div className="col-6 card-body-key">{editableTranslate('calculation_test.calculation_model', 'Berechnungsmodell')}</div>
                            <div className="col-6 card-body-value">{editableTranslate(calculation_model[calculationSettings?.calculation_model || ''])}</div>
                          </div>
                          {
                            calculationSettings && ['POINT_VALUE_MODEL_FIXED_WITH_BUDGET', 'POINT_VALUE_MODEL_FIXED_WITHOUT_BUDGET'].includes(calculationSettings.calculation_model) && <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.fixed_point_value', 'Fester Punktwert')}</div>
                              <div className="col-6 card-body-value">{calculationSettings?.fixed_point_value || ''}</div>
                            </div>
                          }
                          <div className="row">
                            <div className="col-6 card-body-key">{editableTranslate('calculation_test.distribution_method', 'Verteilungsmethode')}</div>
                            <div className="col-6 card-body-value">{editableTranslate(distribution_method[calculationSettings?.distribution_method || ''])}</div>
                          </div>
                          <div className="row">
                            <div className="col-6 card-body-key">{editableTranslate('calculation_test.remaining_distribution', 'Restverteilung')}</div>
                            <div className="col-6 card-body-value">{editableTranslate(remaining_distribution[calculationSettings?.remaining_distribution || ''])}</div>
                          </div>
                        </div>

                        {
                          calculationSettings?.payout_conditions_distribution?.startsWith('FLOOR_') && <div className="summary-section">
                            <span className="summary-title">{editableTranslate('calculation_test.floor', 'Untergrenze')}</span>

                            <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.floor_amount', 'Betrag')}</div>
                              <div className="col-6 card-body-value">
                                {
                                  calculationSettings?.floor ? (calculationSettings?.floor_type === 'FLOOR_AMOUNT' ? ('€' + formatMoney(calculationSettings?.floor)) : (calculationSettings?.floor + '%')) : ''
                                }
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.floor_payout_method', 'Verteilung')}</div>
                              <div className="col-6 card-body-value">{editableTranslate(floor_payout_method[calculationSettings?.floor_payout_method || ''])}</div>
                            </div>
                            <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.floor_considered_employees', 'Zu berücksichtige Beschäftigte')}</div>
                              <div className="col-6 card-body-value">{editableTranslate(floor_considered_employees[calculationSettings?.floor_considered_employees || ''])}</div>
                            </div>
                            <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.floor_factors', 'Faktoren')}</div>
                              <div className="col-6 card-body-value">
                                {
                                  floorFactorsArr.map(a => translate('calculation_test.' + a)).join(', ')
                                }
                              </div>
                            </div>
                          </div>
                        }

                        {
                          calculationSettings?.ceiling_checked && <div className="summary-section">
                            <span className="summary-title">{editableTranslate('calculation_test.ceiling', 'Deckelung')}</span>
                            <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.ceiling_enabled', 'Deckelung aktiviert')}</div>
                              <div className="col-6 card-body-value">true</div>
                            </div>
                            {/* <div className="row">
                              <div className="col-6 card-body-key">{editableTranslate('calculation_test.remaining_considered_employees', 'Remaining considered employees')}</div>
                              <div className="col-6 card-body-value">{editableTranslate(remaining_considered_employees[calculationSettings?.remaining_considered_employees || ''])}</div>
                            </div> */}
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="balancescorecard-setting-card">
                      <div className="card-setting-header font-weight-bold">{editableTranslate('calculation_test.calculation_result', 'Berechnungsergebnis')}</div>
                      <div className="card-setting-body mt-3">
                        {
                          calculationResultTreeData && <div className="col-12">
                            <CalculationResultTreeView jsonData={calculationResultTreeData} employees={defaultEmployees} />
                          </div>
                        }
                        {
                          calculationErrorTreeData &&
                          <>
                            <span className="text-danger font-weight-bold">{editableTranslate('calculation_test.calculation_error', 'Berechnungsfehler')}</span>
                            <div className="row">
                              <CalculationErrorTreeView errorResponse={calculationErrorTreeData} />
                            </div>
                          </>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }

            {
              (calculationSettingsValue && calculationSettings && calculationResult) && <div className="accordion">
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <button data-testid="request-json-toggle" className="btn btn-block text-left btn-accordion" type="button" data-toggle="collapse" data-target="#collapseJson">{editableTranslate('calculation_test.calculation_request_json', 'Berechnungsanfrage JSON')}</button>
                      <Button variant="secondary" onClick={() => copyToClipboard(calculationResult.request)} data-testid="copy-button" className="w-auto"><i className="bi bi-clipboard"></i></Button>
                    </div>
                  </div>

                  <div id="collapseJson" className="collapse">
                    <div className="card-body">
                      <pre>{JSON.stringify(JSON.parse(calculationResult.request), null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>
            }

            {
              (calculationSettingsValue && calculationSettings && calculationResult) && <div className="accordion mt-4">
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <button data-testid="result-toggle" className="btn btn-block text-left btn-accordion" type="button" data-toggle="collapse" data-target="#collapseResult">{editableTranslate('calculation_test.calculation_result', 'Berechnungsergebnis')}</button>
                      <Button variant="secondary" onClick={() => copyToClipboard(calculationResult.response)} data-testid="copy-button" className="w-auto"><i className="bi bi-clipboard"></i></Button>
                    </div>
                  </div>

                  <div id="collapseResult" className="collapse">
                    <div className="card-body">
                      <pre>{JSON.stringify(calculationResult.response, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>
            }

            <Modal
              open={showDeleteConfirm}
              danger
              loading={deleteLoading}
              title={editableTranslate('calculation_test.delete_confirmation_title', 'Testvorlage löschen?')}
              cancelLabel={editableTranslate('form.no')}
              okLabel={editableTranslate('form.delete.yes')}
              onClose={() => setShowDeleteConfirm(false)}
              onConfirm={handleDelete}
              testIdPrefix="calculation-test-delete-"
            >
              {editableTranslate('calculation_test.delete_confirmation')}
            </Modal>

            <div className={`modal default-modal ${showSaveAsModal && 'fade show d-block'}`} tabIndex="-1" data-testid="save-as-modal">
              <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editableTranslate('button.save_as')}</h5>
                    <Button variant="ghost" size="inline" onClick={handleHideSaveAsModal} data-testid="close-button">✕</Button>
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleSubmitSaveAs}>
                      <div className="form-group">
                        <Field label={editableTranslate('report_logo.name')}>
                          <Input data-testid="name-input" type="text" className="text-left" value={saveAsName} onChange={e => setSaveAsName(e.target.value)} />
                        </Field>
                      </div>
                      <div className="text-center">
                        <Button type="submit" variant="primary" loading={loadingSaveAs} data-testid="save-button">{editableTranslate('button.save')}</Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            {
              showSaveAsModal && <div className="modal-backdrop fade show"></div>
            }
          </div>
        </div>
      </div>
    </>
  );
}
